import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Checkbox,
    Table,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Box,
    Autocomplete,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TableBody,
} from '@mui/material';
import {
    getProjectTypes,
    getProjectGenres,
    getProjectLocations,
    getProjectLanguages,
} from '../../services/Project';

const ProjectAttributeTable = ({ onAttributesChange, moduleAttributes, initialAttributes }) => {
    console.log("initialAttributes", initialAttributes);
    const [title, setTitle] = useState([]);
    const [synopsis, setSynopsis] = useState([]);
    const [genres, setGenres] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [customGenres, setCustomGenres] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [selectedProjectTypes, setSelectedProjectTypes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [budget, setBudget] = useState({ operator: 'equal_to', value: '' });
    const [languages, setLanguages] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [startDate, setStartDate] = useState({ from: '', to: '' });
    const [endDate, setEndDate] = useState({ from: '', to: '' });
    const [targetPlatforms, setTargetPlatforms] = useState([]);
    const [projectStatus, setProjectStatus] = useState([]);
    const [producer, setProducer] = useState(false);
    const [watchLink, setWatchLink] = useState(false);
    const [posterImage, setPosterImage] = useState(false);
    const [investor, setInvestor] = useState(false);
    const [distributor, setDistributor] = useState(false);
    const [trailerLink, setTrailerLink] = useState(false);
    const [team, setTeam] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [checkedAttributes, setCheckedAttributes] = useState({});

    const debounceTimer = useRef(null);
    const isInitialized = useRef(false);

    const memoizedOnAttributesChange = useCallback(onAttributesChange, [onAttributesChange]);

    // Existing fetchProjectLocations and handleDebouncedFetch...
    const fetchProjectLocations = useCallback(async (search = '') => {
        if (search.length < 3) {
            setLocations([]);
            return;
        }
        setLoadingLocations(true);
        try {
            const res = await getProjectLocations(search);
            setLocations(res.data || []);
        } catch (error) {
            console.error('Error fetching project locations:', error);
        } finally {
            setLoadingLocations(false);
        }
    }, []);

    const handleDebouncedFetch = useCallback(
        (value) => {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                fetchProjectLocations(value);
            }, 400);
        },
        [fetchProjectLocations]
    );

    useEffect(() => {
        return () => clearTimeout(debounceTimer.current);
    }, []);

    const handleInputChange = useCallback(
        (event, value, reason) => {
            if (reason === 'input') {
                handleDebouncedFetch(value);
            }
        },
        [handleDebouncedFetch]
    );

    const handleCheckboxChange = useCallback(
        (attribute) => (event) => {
            const checked = event.target.checked;
            setCheckedAttributes((prev) => ({
                ...prev,
                [attribute]: checked,
            }));
            if (attribute === 'producer') setProducer(checked);
            else if (attribute === 'investor') setInvestor(checked);
            else if (attribute === 'distributor') setDistributor(checked);
            else if (attribute === 'trailer_link') setTrailerLink(checked);
            else if (attribute === 'team') setTeam(checked);
            else if (attribute === 'poster_image') setPosterImage(checked);
            else if (attribute === 'watch_link') setWatchLink(checked);
        },
        []
    );

    // Existing fetch initial data...
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [typesRes, genresRes, languagesRes] = await Promise.all([
                    getProjectTypes(),
                    getProjectGenres(),
                    getProjectLanguages(),
                ]);
                setProjectTypes(typesRes.data || []);
                setGenres(genresRes.data || []);
                setLanguages(languagesRes.languages || []);
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };
        fetchData();
    }, []);

    // Modified initialization for duration and budget
    useEffect(() => {
        if (!initialAttributes || Object.keys(initialAttributes).length === 0 || isInitialized.current) {
            return;
        }

        console.log('Initializing states with initialAttributes:', initialAttributes);

        const initialChecked = {};
        Object.keys(initialAttributes).forEach(attr => {
            initialChecked[attr] = true;
        });
        moduleAttributes.forEach(attr => {
            if (!initialChecked[attr]) {
                initialChecked[attr] = false;
            }
        });
        setCheckedAttributes(initialChecked);

        // Initialize states
        setTitle(prev => initialAttributes.title || prev);
        setSynopsis(prev => initialAttributes.synopsis || prev);
        setSelectedGenres(prev => initialAttributes.genres || prev);
        setCustomGenres(prev => initialAttributes.custom_genres || prev);
        setSelectedProjectTypes(prev => initialAttributes.project_type || prev);
        setSelectedLocations(prev => initialAttributes.location || prev);
        if (initialAttributes.budget) {
            const { greater_than, less_than, equal_to } = initialAttributes.budget;
            if (equal_to) setBudget({ operator: 'equal_to', value: equal_to });
            else if (greater_than) setBudget({ operator: 'greater_than', value: greater_than });
            else if (less_than) setBudget({ operator: 'less_than', value: less_than });
        }
        setSelectedLanguages(prev => initialAttributes.language || prev);
        setStartDate(prev => initialAttributes.start_date || prev);
        setEndDate(prev => initialAttributes.end_date || prev);
        setTargetPlatforms(prev => initialAttributes.target_platforms || prev);
        setProjectStatus(prev => initialAttributes.project_status || prev);
        setProducer(!!initialAttributes.producer);
        setWatchLink(!!initialAttributes.watch_link);
        setPosterImage(!!initialAttributes.poster_image);
        setInvestor(!!initialAttributes.investor);
        setDistributor(!!initialAttributes.distributor);
        setTrailerLink(!!initialAttributes.trailer_link);
        setTeam(!!initialAttributes.team);

        isInitialized.current = true;
        console.log('States initialized successfully');
    }, [initialAttributes, moduleAttributes]);

    // Modified buildRequiredAttributes
    const buildRequiredAttributes = useCallback(() => {
        const required_attributes = {};

        if (checkedAttributes.title) required_attributes.title = title;
        if (checkedAttributes.synopsis) required_attributes.synopsis = synopsis;
        if (checkedAttributes.genres) required_attributes.genres = selectedGenres;
        if (checkedAttributes.custom_genres) required_attributes.custom_genres = customGenres;
        if (checkedAttributes.project_type) required_attributes.project_type = selectedProjectTypes;
        if (checkedAttributes.location) required_attributes.location = selectedLocations;
        if (checkedAttributes.budget) required_attributes.budget = { [budget.operator]: budget.value };
        if (checkedAttributes.language) required_attributes.language = selectedLanguages;
        if (checkedAttributes.start_date) required_attributes.start_date = startDate;
        if (checkedAttributes.end_date) required_attributes.end_date = endDate;
        if (checkedAttributes.target_platforms) required_attributes.target_platforms = targetPlatforms;
        if (checkedAttributes.project_status) required_attributes.project_status = projectStatus;
        if (checkedAttributes.producer) required_attributes.producer = producer;
        if (checkedAttributes.investor) required_attributes.investor = investor;
        if (checkedAttributes.distributor) required_attributes.distributor = distributor;
        if (checkedAttributes.trailer_link) required_attributes.trailer_link = trailerLink;
        if (checkedAttributes.team) required_attributes.team = team;
        if (checkedAttributes.poster_image) required_attributes.poster_image = posterImage;
        if (checkedAttributes.watch_link) required_attributes.watch_link = watchLink;

        return required_attributes;
    }, [
        checkedAttributes,
        title, synopsis, selectedGenres, customGenres, selectedProjectTypes,
        selectedLocations,
        budget, selectedLanguages, startDate,
        endDate, targetPlatforms, projectStatus, producer, investor,
        distributor, trailerLink, team, posterImage, watchLink
    ]);

    // Existing useEffect for notifying parent
    useEffect(() => {
        const required_attributes = buildRequiredAttributes();
        console.log('Sending required_attributes to parent:', required_attributes);
        memoizedOnAttributesChange({ required_attributes });
    }, [buildRequiredAttributes, memoizedOnAttributesChange]);

    const renderAttributeInput = (attribute) => {
        const chipAttributes = ['title', 'synopsis', 'custom_genres'];
        const booleanAttributes = ['producer', 'investor', 'distributor', 'trailer_link', 'watch_link', 'team', 'poster_image'];

        if (booleanAttributes.includes(attribute)) {
            return null;
        }

        if (chipAttributes.includes(attribute)) {
            return (
                <Box>
                    <Autocomplete
                        freeSolo
                        multiple
                        size="small"
                        fullWidth
                        options={[]}
                        value={
                            attribute === 'title' ? title :
                                attribute === 'synopsis' ? synopsis :
                                    customGenres
                        }
                        onChange={(event, newValue) => {
                            if (attribute === 'title') setTitle(newValue);
                            else if (attribute === 'synopsis') setSynopsis(newValue);
                            else setCustomGenres(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={attribute.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                placeholder="Type a word and press ENTER"
                                required={checkedAttributes[attribute]}
                            />
                        )}
                    />
                </Box>
            );
        }

        if (attribute === 'location') {
            return (
                <Autocomplete
                    multiple
                    size="small"
                    options={locations}
                    getOptionLabel={(option) =>
                        option.city && option.country ? `${option.city}, ${option.country}` : ''
                    }
                    value={selectedLocations}
                    onChange={(event, newValue) => setSelectedLocations(newValue)}
                    onInputChange={handleInputChange}
                    loading={loadingLocations}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Location"
                            placeholder="Type at least 3 letters"
                            required={checkedAttributes.location}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingLocations ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.city && option.country ? `${option.city}, ${option.country}` : ''}
                        </li>
                    )}
                />
            );
        }

        if (attribute === 'language') {
            return (
                <Autocomplete
                    size="small"
                    multiple
                    options={languages}
                    getOptionLabel={(option) => option.language}
                    value={selectedLanguages}
                    onChange={(event, newValue) => setSelectedLanguages(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select Language"
                            required={checkedAttributes.language}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.language}
                        </li>
                    )}
                />
            );
        }

        if (attribute === 'genres') {
            return (
                <Autocomplete
                    size="small"
                    multiple
                    options={genres}
                    getOptionLabel={(option) => option.name}
                    value={selectedGenres}
                    onChange={(event, newValue) => setSelectedGenres(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Select Genre" required={checkedAttributes.genres} />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    )}
                />
            );
        }

        if (attribute === 'project_type') {
            return (
                <Autocomplete
                    size="small"
                    multiple
                    options={projectTypes}
                    getOptionLabel={(option) => option.project_type_name}
                    value={selectedProjectTypes}
                    onChange={(event, newValue) => setSelectedProjectTypes(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Select Project Type" required={checkedAttributes.project_type} />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.work_project_type_id}>
                            {option.project_type_name}
                        </li>
                    )}
                />
            );
        }

        if (['target_platforms', 'project_status'].includes(attribute)) {
            const options = {
                target_platforms: ['Netflix', 'YouTube', 'Amazon Prime', 'Disney+ Hotstar', 'Mx Player', 'SonyLIV', 'Zee5', 'Mubi'],
                project_status: ['Pre-production', 'Production', 'Post-production', 'Completed', 'Released', 'On Hold', 'Cancelled', 'Development'],
            }[attribute] || [];

            return (
                <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={options}
                    getOptionLabel={(option) => option}
                    value={
                        attribute === 'target_platforms' ? targetPlatforms :
                            projectStatus
                    }
                    onChange={(event, newValue) => {
                        if (attribute === 'target_platforms') setTargetPlatforms(newValue);
                        else setProjectStatus(newValue);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={`Select ${attribute.replace(/_/g, ' ')}`}
                            required={checkedAttributes[attribute]}
                        />
                    )}
                />
            );
        }

        if (attribute === 'budget') {
            return (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Operator</InputLabel>
                        <Select
                            value={budget.operator}
                            label="Operator*"
                            onChange={(e) => setBudget({ operator: e.target.value, value: budget.value })}
                            required={checkedAttributes[attribute]}
                        >
                            <MenuItem value="greater_than">Greater Than</MenuItem>
                            <MenuItem value="less_than">Less Than</MenuItem>
                            <MenuItem value="equal_to">Equal To</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label={budget.operator.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        size="small"
                        placeholder='e.g., 5000'
                        value={budget.value}
                        onChange={(e) => setBudget({ operator: budget.operator, value: e.target.value })}
                        required={checkedAttributes[attribute]}
                        sx={{ flexGrow: 1 }}
                    />
                </Box>
            );
        }
        if (attribute === 'start_date' || attribute === 'end_date') {
            return (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        fullWidth
                        value={attribute === 'start_date' ? startDate.from : endDate.from}
                        onChange={(e) =>
                            attribute === 'start_date'
                                ? setStartDate((prev) => ({ ...prev, from: e.target.value }))
                                : setEndDate((prev) => ({ ...prev, from: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        required={checkedAttributes[attribute]}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        fullWidth
                        value={attribute === 'start_date' ? startDate.to : endDate.to}
                        onChange={(e) =>
                            attribute === 'start_date'
                                ? setStartDate((prev) => ({ ...prev, to: e.target.value }))
                                : setEndDate((prev) => ({ ...prev, to: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        required={checkedAttributes[attribute]}
                    />
                </Box>
            );
        }

        return <TextField placeholder="Write the value..." size="small" fullWidth disabled />;
    };

    return (
        <Table sx={{ minWidth: 650 }} aria-label="project attributes table" size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: '40px', fontWeight: 'bold' }}></TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Attributes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {moduleAttributes.map((attribute) => (
                    <TableRow key={attribute}>
                        <TableCell sx={{ width: '40px' }}>
                            <Checkbox
                                checked={checkedAttributes[attribute] || false}
                                onChange={handleCheckboxChange(attribute)}
                            />
                        </TableCell>
                        <TableCell>{attribute.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</TableCell>
                        <TableCell>{renderAttributeInput(attribute)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default ProjectAttributeTable;